// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_ORDER_VIEW_COMPLETE_ADDRESS_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_BUSINESS_ID_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_BUSINESS_NAME_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_CART_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_EMAIL_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_ID_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_MOBILE_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_NAME_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_NO_CLIENT_FOUND_TEXT,
    T_MY_ORDER_VIEW_COMPLETE_PHONE_LABEL,
    T_MY_ORDER_VIEW_COMPLETE_TITLE
} from "../../../../../core/constants/translation";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { isArray } from "../../../../../../../io/hyperify/core/types/Array";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { CartDetails } from "../../../common/cart/CartDetails";
import { OrderDTO } from "../../../../../../../io/hyperify/core/store/types/order/OrderDTO";
import { useMyClientById } from "../../../../hooks/useMyClientById";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import "./MyOrderCompleteArticle.scss";

export interface MyOrderCompleteArticleProps {
    readonly className?: string;
    readonly t       : TranslationFunction;
    readonly session : AuthSessionData;
    readonly order   : OrderDTO;
}

export function MyOrderCompleteArticle (props: MyOrderCompleteArticleProps) {

    const className = props?.className;
    const t = props?.t;
    const order = props?.order;
    const clientId = order?.clientId;
    const cart = order?.cart;

    const [client] = useMyClientById(clientId);

    if (client === undefined) {
        return <Loader />;
    }

    if (!client) {
        return (
            <article
                className={
                    MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME
                    + (className ? ` ${className}` : '')
                }
            >
                <h3>{t(T_MY_ORDER_VIEW_COMPLETE_TITLE)}</h3>
                <p>{t(T_MY_ORDER_VIEW_COMPLETE_NO_CLIENT_FOUND_TEXT)}</p>
            </article>
        );
    }

    return (
        <article
            className={
                MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >
            <Form className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-form`}
            >

                <h3>{t(T_MY_ORDER_VIEW_COMPLETE_TITLE)}</h3>

                <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details`}>

                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-id`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_ID_LABEL)}</strong><br />
                        {client.id}
                    </div>

                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-name`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_NAME_LABEL)}</strong><br />
                        {client.firstname} {client.lastname}
                    </div>

                    {client?.company ? (
                        <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-business-name`}>
                            <strong>{t(T_MY_ORDER_VIEW_COMPLETE_BUSINESS_NAME_LABEL)}</strong><br />
                            {client.company}
                        </div>
                    ) : null}

                    {client?.companyCode ? (
                        <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-business-id`}>
                            <strong>{t(T_MY_ORDER_VIEW_COMPLETE_BUSINESS_ID_LABEL)}</strong><br />
                            {client.companyCode}
                        </div>
                    ): null}

                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-email`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_EMAIL_LABEL)}</strong><br />
                        {client?.email}
                    </div>

                    {client?.phone ? (
                        <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-phone`}>
                            <strong>{t(T_MY_ORDER_VIEW_COMPLETE_PHONE_LABEL)}</strong><br />
                            {client?.phone}
                        </div>
                    ) : null}

                    {client?.mobile ? (
                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-mobile`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_MOBILE_LABEL)}</strong><br />
                        {client?.mobile}
                    </div>
                    ) : null}

                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-address`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_ADDRESS_LABEL)}</strong><br />
                        {ComponentUtils.splitWithLineBreaks( isArray(client?.address) ? client.address.join('\n') : '')}<br />
                        {client?.postCode} {client?.postName}<br />
                        {client?.country}
                    </div>

                    <div className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-details-field details-cart`}>
                        <strong>{t(T_MY_ORDER_VIEW_COMPLETE_CART_LABEL)}</strong><br />
                        <CartDetails
                            className={`${MY_ORDER_COMPLETE_ARTICLE_CLASS_NAME}-cart-details`}
                            t={t}
                            cart={cart}
                        />
                    </div>

                </div>

            </Form>
        </article>
    );

}
